'use client';

// ════════════════════════════════════════════════════════════════════════
// Bandeau de consentement cookies (RGPD) — site vitrine Ligobox.
//
// Pilote le « Consent Mode v2 » de Google : au 1er chargement, le <head>
// (layout.jsx) pose un consentement PAR DEFAUT « denied » AVANT le chargement
// de GTM. Ce composant met a jour le consentement (gtag consent update) selon
// le choix du visiteur, et memorise ce choix dans localStorage.
//
// Entierement personnalisable depuis Super-Admin -> Site vitrine -> General
// (textes, lien politique cookies, couleurs de fond, couleurs et textes des
// boutons) via l'objet `config` (settings.cookie_banner), passe en prop.
//
// Le lien « Gerer les cookies » du footer reouvre ce bandeau (retrait du
// consentement, exigence RGPD) via l'evenement `ligobox:open-cookie-prefs`
// ou window.__lbOpenCookiePrefs().
// ════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

const STORAGE_KEY = 'lb_cookie_consent_v1';

// Envoie une mise a jour de consentement a Google (via dataLayer/gtag).
function pushConsentUpdate(choices) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  gtag('consent', 'update', {
    ad_storage: choices.ads ? 'granted' : 'denied',
    ad_user_data: choices.ads ? 'granted' : 'denied',
    ad_personalization: choices.ads ? 'granted' : 'denied',
    analytics_storage: choices.analytics ? 'granted' : 'denied',
  });
}

export default function CookieConsent({ config = {} }) {
  const c = config || {};
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [ads, setAds] = useState(false);

  // Lecture du choix memorise + branchement de la reouverture (footer).
  useEffect(() => {
    let stored = null;
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (e) {
      stored = null;
    }
    if (!stored || !stored.choices) {
      setOpen(true); // 1re visite : on affiche le bandeau
    } else {
      setAnalytics(!!stored.choices.analytics);
      setAds(!!stored.choices.ads);
    }
    const reopen = () => { setShowPrefs(true); setOpen(true); };
    window.__lbOpenCookiePrefs = reopen;
    window.addEventListener('ligobox:open-cookie-prefs', reopen);
    return () => window.removeEventListener('ligobox:open-cookie-prefs', reopen);
  }, []);

  const persist = (choices) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ v: 1, ts: Date.now(), choices }));
    } catch (e) { /* stockage indispo : on continue quand meme */ }
    pushConsentUpdate(choices);
  };

  const acceptAll = () => {
    setAnalytics(true); setAds(true);
    persist({ analytics: true, ads: true });
    setOpen(false); setShowPrefs(false);
  };
  const refuseAll = () => {
    setAnalytics(false); setAds(false);
    persist({ analytics: false, ads: false });
    setOpen(false); setShowPrefs(false);
  };
  const saveChoices = () => {
    persist({ analytics, ads });
    setOpen(false); setShowPrefs(false);
  };

  if (c.enabled === false) return null;
  if (!open) return null;

  // ── Couleurs (avec repli sur le theme si non defini) ──────────────────
  const bg = c.bg_color || '#ffffff';
  const text = c.text_color || '#0f172a';
  const acceptBg = (c.accept_bg && String(c.accept_bg).trim()) || 'var(--color-primary)';
  const acceptText = c.accept_text_color || '#ffffff';
  const refuseBg = c.refuse_bg || '#f1f5f9';
  const refuseText = c.refuse_text_color || text;
  const muted = 'rgba(100,116,139,0.95)';

  const title = c.title != null ? c.title : 'Nous respectons votre vie privee';
  const message = c.message != null ? c.message
    : "Nous utilisons des cookies pour mesurer l'audience du site et ameliorer votre experience. Vous pouvez tout accepter, tout refuser, ou personnaliser vos choix.";
  const acceptLabel = c.accept_text || 'Tout accepter';
  const refuseLabel = c.refuse_text || 'Tout refuser';
  const customizeLabel = c.customize_text || 'Personnaliser';
  const saveLabel = c.save_text || 'Enregistrer mes choix';

  const btnBase = {
    padding: '11px 20px',
    borderRadius: 'var(--radius-buttons, 10px)',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
    border: 'none',
    lineHeight: 1.2,
    fontFamily: 'inherit',
  };

  return (
    <div
      role="dialog"
      aria-label="Consentement aux cookies"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 2147483000,
        maxWidth: 720,
        margin: '0 auto',
        background: bg,
        color: text,
        border: '1px solid rgba(15,23,42,0.10)',
        borderRadius: 16,
        boxShadow: '0 24px 60px -18px rgba(15,23,42,0.45)',
        padding: 'clamp(18px, 3vw, 26px)',
        fontFamily: "'Inter', system-ui, sans-serif",
      }}
    >
      {title ? (
        <div style={{ fontSize: 17, fontWeight: 800, marginBottom: 8, letterSpacing: '-0.01em' }}>
          {title}
        </div>
      ) : null}

      <p style={{ fontSize: 13.5, lineHeight: 1.6, margin: 0, color: text, opacity: 0.9 }}>
        {message}
        {c.policy_url ? (
          <>
            {' '}
            <a
              href={c.policy_url}
              style={{ color: 'var(--color-primary)', textDecoration: 'underline', fontWeight: 600 }}
            >
              {c.policy_link_text || 'En savoir plus'}
            </a>
          </>
        ) : null}
      </p>

      {/* Panneau « Personnaliser » : cases mesure d'audience / publicite */}
      {showPrefs ? (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <PrefRow
            checked={analytics}
            onChange={setAnalytics}
            label={c.analytics_label || "Mesure d'audience"}
            desc={c.analytics_desc || 'Google Analytics : statistiques de visite anonymes.'}
            text={text}
            muted={muted}
          />
          <PrefRow
            checked={ads}
            onChange={setAds}
            label={c.ads_label || 'Publicite et marketing'}
            desc={c.ads_desc || 'Google Ads, Meta : mesure des conversions et publicites.'}
            text={text}
            muted={muted}
          />
        </div>
      ) : null}

      <div
        style={{
          marginTop: 18,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
        }}
      >
        {showPrefs ? (
          <button type="button" onClick={saveChoices} style={{ ...btnBase, background: acceptBg, color: acceptText }}>
            {saveLabel}
          </button>
        ) : (
          <button type="button" onClick={acceptAll} style={{ ...btnBase, background: acceptBg, color: acceptText }}>
            {acceptLabel}
          </button>
        )}

        <button type="button" onClick={refuseAll} style={{ ...btnBase, background: refuseBg, color: refuseText }}>
          {refuseLabel}
        </button>

        {!showPrefs ? (
          <button
            type="button"
            onClick={() => setShowPrefs(true)}
            style={{
              ...btnBase,
              background: 'transparent',
              color: muted,
              textDecoration: 'underline',
              padding: '11px 8px',
              fontWeight: 600,
            }}
          >
            {customizeLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}

// Une ligne de preference (case a cocher + libelle + description).
function PrefRow({ checked, onChange, label, desc, text, muted }) {
  return (
    <label
      style={{
        display: 'flex',
        gap: 12,
        alignItems: 'flex-start',
        padding: '12px 14px',
        border: '1px solid rgba(15,23,42,0.10)',
        borderRadius: 10,
        cursor: 'pointer',
        background: 'rgba(148,163,184,0.06)',
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ width: 18, height: 18, marginTop: 2, accentColor: 'var(--color-primary)', cursor: 'pointer', flexShrink: 0 }}
      />
      <span style={{ display: 'block' }}>
        <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: text }}>{label}</span>
        <span style={{ display: 'block', fontSize: 12.5, color: muted, marginTop: 2, lineHeight: 1.5 }}>{desc}</span>
      </span>
    </label>
  );
}
